import React, { useEffect, useRef, useState } from "react";
import { Wallet, TrendingUp, Coins, Signal, Cpu } from "lucide-react";
import { fmtMoney, fmtSigned, fmtPct } from "../../lib/format";

function useFlash(value) {
  const prev = useRef(value);
  const [cls, setCls] = useState("");
  useEffect(() => {
    if (value == null || prev.current == null) {
      prev.current = value;
      return;
    }
    if (value > prev.current) setCls("tick-up");
    else if (value < prev.current) setCls("tick-down");
    prev.current = value;
    const t = setTimeout(() => setCls(""), 600);
    return () => clearTimeout(t);
  }, [value]);
  return cls;
}

function Card({ testid, title, icon, value, valueClass = "", sub, badge, badgeClass, accent = "#F59E0B", flash }) {
  const flashCls = useFlash(flash);
  return (
    <div
      data-testid={testid}
      className="panel relative overflow-hidden p-4 float-in"
      style={{ borderTop: `2px solid ${accent}` }}
    >
      <div className="flex items-center justify-between">
        <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">{title}</span>
        <span className="text-slate-500">{icon}</span>
      </div>
      <div className={`font-mono text-2xl lg:text-[26px] font-extrabold tracking-tight mt-3 ${valueClass} ${flashCls}`}>
        {value}
      </div>
      <div className="flex items-center justify-between mt-2">
        <span className="text-[10px] text-slate-500">{sub}</span>
        {badge && (
          <span className={`text-[9px] font-mono font-bold px-1.5 py-0.5 rounded ${badgeClass}`}>{badge}</span>
        )}
      </div>
    </div>
  );
}

export default function KpiCards({ snapshot }) {
  const acc = snapshot?.account || {};
  const stats = snapshot?.stats || {};
  const live = snapshot?.liveEnabled;
  const currency = acc.currency || "EUR";
  const profit = Number(acc.profit || 0);
  const balance = Number(acc.balance || 0);
  const pnlPct = balance ? (profit / balance) * 100 : 0;
  const ai = (snapshot?.signals || []).find((s) => s.aiConsensus)?.aiConsensus;
  const usedMarginPct =
    acc.equity && acc.freeMargin != null ? ((acc.equity - acc.freeMargin) / acc.equity) * 100 : 0;
  const totalDecided = (stats.wins || 0) + (stats.losses || 0);
  const winRate = totalDecided ? ((stats.wins || 0) / totalDecided) * 100 : 0;

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
      <Card
        testid="kpi-card-equity"
        title="Equity Conto"
        icon={<Wallet size={15} />}
        value={fmtMoney(acc.equity, currency)}
        flash={acc.equity}
        sub={live ? "MT5 live" : "conto demo simulato"}
        badge={live ? "LIVE" : "DEMO"}
        badgeClass={live ? "bg-amber-500/15 text-amber-400" : "bg-sky-500/15 text-sky-400"}
      />
      <Card
        testid="kpi-card-open-pnl"
        title="Profitto / Perdita Aperto"
        icon={<TrendingUp size={15} />}
        value={fmtSigned(profit, currency)}
        valueClass={profit >= 0 ? "text-emerald-400" : "text-rose-400"}
        flash={profit}
        sub={`${(snapshot?.positions || []).length} posizioni in corso`}
        badge={fmtPct(pnlPct)}
        badgeClass={profit >= 0 ? "bg-emerald-500/15 text-emerald-400" : "bg-rose-500/15 text-rose-400"}
        accent={profit >= 0 ? "#10B981" : "#EF4444"}
      />
      <Card
        testid="kpi-card-free-margin"
        title="Capitale Disponibile"
        icon={<Coins size={15} />}
        value={fmtMoney(acc.freeMargin, currency)}
        flash={acc.freeMargin}
        sub={`Margine utilizzato: ${usedMarginPct.toFixed(1).replace(".", ",")}%`}
      />
      <Card
        testid="kpi-card-signals-today"
        title="Segnali Oggi (XAUUSD)"
        icon={<Signal size={15} />}
        value={`${stats.executedToday || 0} Eseguiti`}
        sub={`Win-rate: ${winRate.toFixed(1).replace(".", ",")}%`}
        accent="#38BDF8"
      />
      <Card
        testid="kpi-card-ai-agreement"
        title="Accordo Modelli AI"
        icon={<Cpu size={15} />}
        value={ai ? `${Math.round((ai.agreement || 0) * 100)}%` : "—"}
        sub={ai ? `${(ai.models || []).filter((m) => !m.error).length} modelli · ${ai.decision}` : "nessuna analisi"}
        badge={ai?.decision || "WAIT"}
        badgeClass={
          ai?.decision === "BUY"
            ? "bg-emerald-500/15 text-emerald-400"
            : ai?.decision === "SELL"
            ? "bg-rose-500/15 text-rose-400"
            : "bg-amber-500/15 text-amber-400"
        }
      />
    </div>
  );
}
